<template>
    <router-link
      :to="vacancy.is_our_vacancy ? '' : `/vacancy/${vacancy.id}`"
      class="vacancy-item"
      :class="{ 'our-vacancy': vacancy.is_our_vacancy }"
      @click.prevent="openVacancy"
    >
  
      <!-- =====================================================
           OUR VACANCY
      ====================================================== -->
  
      <div
        v-if="vacancy.is_our_vacancy"
        class="our-vacancy-badge"
      >
        ✨ Ваша вакансия
      </div>
  
  
      <!-- =====================================================
           IMAGE
      ====================================================== -->
  
      <div class="image-container">
  
        <div class="image-top-actions">
  
          <div class="city-label">
            {{ cityLabel }}
          </div>
  
  
          <button
            type="button"
            class="like-button"
            aria-label="Добавить в избранное"
            @click.stop.prevent="toggleLike"
          >
            <img
              :src="
                isLiked
                  ? '/like_active.png'
                  : '/like_not_active.png'
              "
              alt=""
            />
          </button>
  
        </div>
  
  
        <div
          v-if="loading"
          class="spinner-overlay"
        >
          <div class="spinner"></div>
        </div>
  
  
        <img
          v-if="vacancy.image_url"
          :src="vacancy.image_url"
          alt="Вакансия"
          class="vacancy-image"
          @load="loading = false"
        />
  
  
        <div
          v-else
          class="image-placeholder"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <rect
              x="4"
              y="5"
              width="16"
              height="14"
              rx="3"
              stroke="currentColor"
              stroke-width="1.5"
            />
  
            <path
              d="M7 15L10.5 11.5L13 14L15 12L18 15"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
  
      </div>
  
  
      <!-- =====================================================
           CONTENT
      ====================================================== -->
  
      <div class="vacancy-content">
  
        <h3 class="vacancy-title">
          {{ vacancy.title || "Название вакансии" }}
        </h3>
  
  
        <p class="vacancy-description">
          {{
            vacancy.info
            || "Описание вакансии пока не указано."
          }}
        </p>
  
  
        <div class="vacancy-footer">
  
          <span class="open-label">
            Подробнее
          </span>
  
  
          <span class="open-arrow">
            <svg
              viewBox="0 0 18 18"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6.5 4.5L11 9L6.5 13.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
  
        </div>
  
      </div>
  
    </router-link>
  </template>
  
  
  <script setup>
  
  import {
    computed,
    ref,
    watch
  } from "vue";
  
  
  const props = defineProps({
    vacancy: {
      type: Object,
      required: true
    }
  });
  
  
  const emit = defineEmits([
    "toggle-favorite",
    "open-vacancy",
    "open-our-vacancy"
  ]);
  
  
  const loading = ref(
    Boolean(props.vacancy?.image_url)
  );
  
  
  /* ============================================================
     LIKE
  ============================================================ */
  
  const isLiked = computed(() => {
    return props.vacancy?.is_liked ?? false;
  });
  
  
  const toggleLike = () => {
  
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  
  
    const raw =
      props.vacancy?.id
      ?? props.vacancy?.vacancyID
      ?? props.vacancy?.vacancy_id;
  
  
    const vacancyId =
      typeof raw === "object"
        ? (
            raw?.id
            ?? raw?.vacancyID
            ?? raw?.vacancy_id
            ?? ""
          )
        : String(raw ?? "");
  
  
    emit("toggle-favorite", {
      vacancyId,
  
      message:
        isLiked.value
          ? "Вакансия удалена из избранного"
          : "Вакансия добавлена в избранное ⭐"
    });
  
  };
  
  
  /* ============================================================
     OPEN
  ============================================================ */
  
  const openVacancy = () => {
  
    if (!props.vacancy) {
      return;
    }
  
  
    if (
      props.vacancy.is_our_vacancy
    ) {
  
      emit(
        "open-our-vacancy",
        props.vacancy
      );
  
      return;
    }
  
  
    emit(
      "open-vacancy",
      props.vacancy
    );
  
  };
  
  
  /* ============================================================
     REGION
  ============================================================ */
  
  const regions = {
    1: "Москва",
    2: "Санкт-Петербург"
  };
  
  
  const getCityName = id => {
    return (
      regions[Number(id)]
      || "Регион не указан"
    );
  };
  
  
  const cityLabel = computed(() => {
  
    if (
      props.vacancy?.is_remote
    ) {
      return "Удалёнка";
    }
  
  
    return getCityName(
      props.vacancy?.city_id
    );
  
  });
  
  
  /* ============================================================
     IMAGE LOADING
  ============================================================ */
  
  watch(
    () => props.vacancy?.image_url,
  
    value => {
      loading.value =
        Boolean(value);
    }
  );
  
  </script>
  
  
  <style scoped>
  
  /* ============================================================
     ROOT
  ============================================================ */
  
.vacancy-item {
  position: relative;

  width: 375px;
  height: auto;
  min-height: 228px;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  box-sizing: border-box;

  background: #ffffff;

  border: 1px solid rgba(25, 40, 60, 0.08);
  border-radius: 18px;

  box-shadow:
    0 10px 28px rgba(15, 23, 42, 0.045);

  color: inherit;
  text-decoration: none;

  cursor: pointer;

  transition:
    transform 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}
  
  
  .vacancy-item:hover {
    transform:
      translateY(-2px);
  
    border-color:
      rgba(40, 110, 205, 0.12);
  
    box-shadow:
      0
      16px
      34px
      rgba(15, 23, 42, 0.09);
  }
  
  
  /* ============================================================
     IMAGE
  ============================================================ */
  
  .image-container {
    position: relative;
  
    width: 100%;
    height: 145px;
  
    flex:
      0
      0
      145px;
  
    overflow: hidden;
  
    background:
      #edf0f4;
  }
  
  
  .vacancy-image {
    width: 100%;
    height: 100%;
  
    display: block;
  
    object-fit: cover;
  }
  
  
  .image-container::after {
    content: "";
  
    position: absolute;
  
    inset: 0;
  
    pointer-events: none;
  
    background:
      linear-gradient(
        180deg,
        rgba(0,0,0,0.08),
        transparent 42%,
        rgba(0,0,0,0.04)
      );
  }
  
  
  /* ============================================================
     PLACEHOLDER
  ============================================================ */
  
  .image-placeholder {
    width: 100%;
    height: 100%;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    color: #9ea5af;
  
    background:
      linear-gradient(
        135deg,
        #eef2f7,
        #e8edf4
      );
  }
  
  
  .image-placeholder svg {
    width: 30px;
    height: 30px;
  }
  
  
  /* ============================================================
     IMAGE ACTIONS
  ============================================================ */
  
  .image-top-actions {
    position: absolute;
  
    top: 10px;
    left: 10px;
    right: 10px;
  
    z-index: 10;
  
    display: flex;
    align-items: center;
    justify-content: space-between;
  
    pointer-events: none;
  }
  
  
  .city-label {
    min-height: 27px;
  
    display: inline-flex;
    align-items: center;
  
    padding:
      0
      10px;
  
    color: #ffffff;
  
    background:
      rgba(25, 30, 40, 0.48);
  
    border:
      1px solid
      rgba(255,255,255,0.22);
  
    border-radius: 9px;
  
    backdrop-filter:
      blur(10px);
  
    -webkit-backdrop-filter:
      blur(10px);
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 8px;
  
    pointer-events: auto;
  }
  
  
  /* ============================================================
     LIKE
  ============================================================ */
  
  .like-button {
    width: 31px;
    height: 31px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    padding: 0;
  
    background:
      rgba(25, 30, 40, 0.48);
  
    border:
      1px solid
      rgba(255,255,255,0.22);
  
    border-radius: 9px;
  
    backdrop-filter:
      blur(10px);
  
    -webkit-backdrop-filter:
      blur(10px);
  
    cursor: pointer;
  
    pointer-events: auto;
  
    transition:
      transform 0.18s ease,
      background-color 0.18s ease;
  }
  
  
  .like-button img {
    width: 16px;
    height: 14px;
  
    object-fit: contain;
  }
  
  
  .like-button:hover {
    transform:
      scale(1.05);
  
    background:
      rgba(25, 30, 40, 0.62);
  }
  
  
  .like-button:active {
    transform:
      scale(0.92);
  }
  
  
  /* ============================================================
     CONTENT
  ============================================================ */
  
  .vacancy-content {
    min-height: 0;
  
    flex: 1;
  
    display: flex;
    flex-direction: column;
  
    padding:
      14px
      15px
      12px;
  }
  
  
  .vacancy-title {
    margin: 0;
  
    color: #2c3138;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 13px;
  
    line-height: 1.3;
  
    letter-spacing: -0.15px;
  
    display: -webkit-box;
  
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
  
    overflow: hidden;
  }
  
  
  .vacancy-description {
    margin:
      5px
      0
      0;
  
    color: #858b94;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 8px;
  
    line-height: 1.45;
  
    display: -webkit-box;
  
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  
    overflow: hidden;
  }
  
  
  /* ============================================================
     FOOTER
  ============================================================ */
  
  .vacancy-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
  
    margin-top: auto;
  
    padding-top: 7px;
  }
  
  
  .open-label {
    color: #92979f;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 7px;
  }
  
  
  .open-arrow {
    width: 25px;
    height: 25px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    color: #6584b5;
  
    background:
      rgba(60, 105, 175, 0.055);
  
    border-radius: 7px;
  
    transition:
      transform 0.18s ease,
      background-color 0.18s ease;
  }
  
  
  .open-arrow svg {
    width: 12px;
    height: 12px;
  }
  
  
  .vacancy-item:hover
  .open-arrow {
    transform:
      translateX(2px);
  
    background:
      rgba(50, 105, 195, 0.09);
  }
  
  
  /* ============================================================
     LOADER
  ============================================================ */
  
  .spinner-overlay {
    position: absolute;
  
    inset: 0;
  
    z-index: 6;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    background:
      rgba(20, 25, 32, 0.14);
  }
  
  
  .spinner {
    width: 25px;
    height: 25px;
  
    border:
      3px solid
      rgba(255,255,255,0.30);
  
    border-top-color:
      #ffffff;
  
    border-radius: 50%;
  
    animation:
      spin
      0.75s
      linear
      infinite;
  }
  
  
  @keyframes spin {
  
    to {
      transform:
        rotate(360deg);
    }
  
  }
  
  
  /* ============================================================
     OUR VACANCY
  ============================================================ */
  
  .our-vacancy {
    opacity: 0.72;
  }
  
  
  .our-vacancy:hover {
    opacity: 0.9;
  }
  
  
  .our-vacancy-badge {
    position: absolute;
  
    left: 10px;
    bottom: 112px;
  
    z-index: 20;
  
    min-height: 26px;
  
    display: inline-flex;
    align-items: center;
  
    padding:
      0
      9px;
  
    color: #ffffff;
  
    background:
      rgba(30, 35, 43, 0.66);
  
    border:
      1px solid
      rgba(255,255,255,0.16);
  
    border-radius: 8px;
  
    backdrop-filter:
      blur(10px);
  
    -webkit-backdrop-filter:
      blur(10px);
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 7px;
  }
  
  
  /* ============================================================
     DARK
  ============================================================ */
  
  html[data-theme="dark"]
  .vacancy-item {
    background:
      linear-gradient(
        145deg,
        #22242a,
        #1d1f24
      );
  
    border-color:
      rgba(255,255,255,0.065);
  
    box-shadow:
      0
      12px
      30px
      rgba(0,0,0,0.16);
  }
  
  
  html[data-theme="dark"]
  .vacancy-item:hover {
    border-color:
      rgba(255,255,255,0.1);
  
    box-shadow:
      0
      18px
      40px
      rgba(0,0,0,0.28);
  }
  
  
  html[data-theme="dark"]
  .vacancy-title {
    color: #eff0f2;
  }
  
  
  html[data-theme="dark"]
  .vacancy-description {
    color: #858a93;
  }
  
  
  html[data-theme="dark"]
  .open-label {
    color: #70757e;
  }
  
  
  html[data-theme="dark"]
  .open-arrow {
    color: #91b6ee;
  
    background:
      rgba(80,130,210,0.09);
  }
  
  
  html[data-theme="dark"]
  .image-placeholder {
    color: #666c76;
  
    background:
      linear-gradient(
        135deg,
        #2c3037,
        #25282e
      );
  }
  
  
  /* ============================================================
     MOBILE
  ============================================================ */
  
  @media (max-width: 430px) {
  
    .vacancy-item {
      width: 100%;
      height: 255px;
  
      border-radius: 17px;
    }
  
  
    .image-container {
      height: 135px;
  
      flex-basis: 135px;
    }
  
  
    .vacancy-content {
      padding:
        12px
        13px
        10px;
    }
  
  }
  
  
  /* ============================================================
     REDUCED MOTION
  ============================================================ */
  
  @media (prefers-reduced-motion: reduce) {
  
    .vacancy-item,
    .like-button,
    .open-arrow {
      transition: none;
    }
  
  
    .spinner {
      animation: none;
    }
  
  }
  
  </style>